import { useQuery } from '@tanstack/react-query';
import { billing } from '@/api/billing';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, ArrowLeft, FileText } from 'lucide-react';
import { cn } from '@/lib/cn';

function formatDate(unixSec: number) {
  return new Date(unixSec * 1000).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatAmount(cents: number) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

const STATUS_STYLES: Record<string, string> = {
  paid:    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900',
  open:    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900',
  void:    'bg-muted text-muted-foreground border-border',
  draft:   'bg-muted text-muted-foreground border-border',
  uncollectible: 'bg-destructive/10 text-destructive border-destructive/20',
};

export function InvoiceList() {
  const user = useCurrentUser();

  const { data, isLoading } = useQuery({
    queryKey: ['billing-invoices'],
    queryFn: () => billing.listInvoices(),
    enabled: !!user,
  });

  const invoices = data?.rows ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description="Billing history for your subscription."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/billing"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to billing</Link>
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden max-w-3xl">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                {['Invoice', 'Date', 'Period', 'Amount', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    No invoices yet.
                  </td>
                </tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {inv.number ?? inv.id.slice(0, 12)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(inv.created)}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                    {formatDate(inv.period_start)} – {formatDate(inv.period_end)}
                  </td>
                  <td className="px-4 py-3 tabular-nums font-medium">{formatAmount(inv.amount_due)}</td>
                  <td className="px-4 py-3">
                    <span className={cn(
                      'inline-block rounded-full border px-2 py-0.5 text-xs font-semibold capitalize',
                      STATUS_STYLES[inv.status ?? 'draft'] ?? STATUS_STYLES.draft,
                    )}>
                      {inv.status ?? 'draft'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(inv.hosted_invoice_url || inv.invoice_pdf) && (
                      <a
                        href={inv.hosted_invoice_url ?? inv.invoice_pdf ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
